import React, { useState, useEffect, useCallback } from 'react';
import { PorteroLayout } from '../../components/portero/PorteroLayout';
import { getMisBotones, enviarComando, getPorteroUser } from '../../lib/porteroApi';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Zap, Power, DoorOpen, Bell, ShieldCheck, Loader2, WifiOff, Wifi } from 'lucide-react';

const iconMap = {
  zap: Zap,
  power: Power,
  door: DoorOpen,
  bell: Bell,
  shield: ShieldCheck,
};

function PulseButton({ btn, deviceId, onSend, large }) {
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState(false);
  const Icon = iconMap[btn.icono] || Zap;

  const handlePress = async () => {
    setSending(true);
    setFlash(true);
    try {
      const result = await onSend(deviceId, btn.comando, btn.nombre);
      if (result.errors?.length > 0 && result.errors[0]?.includes('Sin conexión')) {
        toast.error(`${btn.nombre}: Dispositivo sin conexión GPRS`, { duration: 3000 });
      } else {
        toast.success(`${btn.nombre}: Comando enviado`, { duration: 2000 });
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSending(false);
    setTimeout(() => setFlash(false), 600);
  };

  const sizeClass = large
    ? 'w-40 h-40 sm:w-48 sm:h-48'
    : 'w-24 h-24 sm:w-28 sm:h-28';
  const iconSize = large ? 'h-16 w-16' : 'h-10 w-10';

  return (
    <button
      onClick={handlePress}
      disabled={sending}
      className="flex flex-col items-center gap-3 group"
      data-testid={`btn-pulso-${btn.nombre?.replace(/\s/g, '-').toLowerCase()}`}
    >
      <div
        className={`${sizeClass} rounded-full flex items-center justify-center transition-all duration-300
          ${flash ? 'scale-110 shadow-[0_0_40px_rgba(16,185,129,0.6)]' : 'shadow-[0_0_15px_rgba(16,185,129,0.2)]'}
          ${sending ? 'opacity-70' : 'active:scale-95 hover:shadow-[0_0_30px_rgba(16,185,129,0.4)]'}
        `}
        style={{ backgroundColor: btn.color || '#10b981' }}
      >
        {sending ? (
          <Loader2 className={`${iconSize} text-white animate-spin`} />
        ) : (
          <Icon className={`${iconSize} text-white`} />
        )}
      </div>
      <span className={`text-zinc-400 font-medium text-center truncate ${large ? 'text-base max-w-[200px]' : 'text-xs max-w-[120px]'}`}>{btn.nombre}</span>
    </button>
  );
}

function ToggleButton({ btn, deviceId, onSend, large }) {
  const [isOn, setIsOn] = useState(false);
  const [sending, setSending] = useState(false);
  const Icon = iconMap[btn.icono] || Power;

  const handleToggle = async () => {
    setSending(true);
    const cmd = isOn ? (btn.comando_off || btn.comando) : btn.comando;
    try {
      const result = await onSend(deviceId, cmd, btn.nombre);
      if (result.errors?.length > 0 && result.errors[0]?.includes('Sin conexión')) {
        toast.error(`${btn.nombre}: Dispositivo sin conexión GPRS`, { duration: 3000 });
      } else {
        setIsOn(!isOn);
        toast.success(`${btn.nombre}: ${!isOn ? 'Activado' : 'Desactivado'}`, { duration: 2000 });
      }
    } catch (e) {
      toast.error(e.message);
    }
    setSending(false);
  };

  const bgColor = isOn ? (btn.color || '#10b981') : '#3f3f46';
  const glowColor = isOn ? `${btn.color || '#10b981'}66` : 'transparent';
  const sizeClass = large ? 'w-40 h-40 sm:w-48 sm:h-48' : 'w-24 h-24 sm:w-28 sm:h-28';
  const iconSize = large ? 'h-16 w-16' : 'h-10 w-10';

  return (
    <button
      onClick={handleToggle}
      disabled={sending}
      className="flex flex-col items-center gap-3 group"
      data-testid={`btn-toggle-${btn.nombre?.replace(/\s/g, '-').toLowerCase()}`}
    >
      <div
        className={`${sizeClass} rounded-full flex items-center justify-center transition-all duration-300
          ${sending ? 'opacity-70' : 'active:scale-95'}
        `}
        style={{
          backgroundColor: bgColor,
          boxShadow: `0 0 ${isOn ? '30' : '10'}px ${glowColor}`,
        }}
      >
        {sending ? (
          <Loader2 className={`${iconSize} text-white animate-spin`} />
        ) : (
          <Icon className={`${iconSize} text-white`} />
        )}
      </div>
      <span className={`text-zinc-400 font-medium text-center truncate ${large ? 'text-base max-w-[200px]' : 'text-xs max-w-[120px]'}`}>{btn.nombre}</span>
      <span className={`text-[10px] font-bold uppercase tracking-wider ${isOn ? 'text-emerald-400' : 'text-zinc-600'}`}>
        {isOn ? 'ON' : 'OFF'}
      </span>
    </button>
  );
}

export default function PorteroPanelPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = getPorteroUser();

  const load = useCallback(async () => {
    try {
      const data = await getMisBotones();
      setConfigs(data.botones || []);
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSend = async (deviceId, command, botonNombre) => {
    return await enviarComando(deviceId, command, botonNombre);
  };

  if (loading) {
    return (
      <PorteroLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-8 w-48 bg-zinc-800" />
          <div className="flex flex-wrap gap-6 justify-center">
            {[1, 2, 3].map(i => <Skeleton key={i} className="w-28 h-36 rounded-2xl bg-zinc-800" />)}
          </div>
        </div>
      </PorteroLayout>
    );
  }

  return (
    <PorteroLayout>
      <div className="p-4 sm:p-6 space-y-8" data-testid="portero-panel">
        {configs.length === 0 ? (
          <div className="text-center py-20">
            <WifiOff className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-zinc-400">Sin botones configurados</h2>
            <p className="text-sm text-zinc-600 mt-2">
              {user?.rol === 'admin'
                ? 'Ve a Configuración para asignar botones a tus dispositivos.'
                : 'Contacta al administrador para configurar tus controles.'}
            </p>
          </div>
        ) : (
          configs.map(config => {
            const totalBtns = configs.reduce((sum, c) => sum + (c.botones?.length || 0), 0);
            const isLarge = totalBtns === 1;
            return (
            <div key={config.id} className="space-y-4">
              <div className="flex items-center gap-3 px-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <h2 className="text-sm font-bold text-zinc-300 uppercase tracking-wider">
                  {config.device_name || `Dispositivo ${config.device_id}`}
                </h2>
              </div>
              <div className={`flex flex-wrap gap-6 ${isLarge ? 'justify-center items-center min-h-[60vh]' : config.layout === 'list' ? 'flex-col items-start' : 'justify-center sm:justify-start'}`}>
                {(config.botones || []).map((btn, i) =>
                  btn.tipo === 'toggle' ? (
                    <ToggleButton key={i} btn={btn} deviceId={config.device_id} onSend={handleSend} large={isLarge} />
                  ) : (
                    <PulseButton key={i} btn={btn} deviceId={config.device_id} onSend={handleSend} large={isLarge} />
                  )
                )}
              </div>
            </div>
            );
          })
        )}
      </div>
    </PorteroLayout>
  );
}
